import React, { useEffect, useMemo, useState } from 'react';
import { repoDealsApi, api } from '../services/api';
import DealSlipViewModal from '../components/DealSlipViewModal';
import DealConfirmationButton from '../components/DealConfirmationButton';
import LimitExceededNotice from '../components/LimitExceededNotice';

const levelLabel = (lvl) => {
  switch (lvl) {
    case 'front_office':
      return 'Front Office';
    case 'back_office_verifier':
      return 'Back Office Verifier';
    case 'back_office_final':
      return 'Back Office Final';
    case 'final_approved':
      return 'Final Approved';
    case 'rejected':
      return 'Rejected';
    default:
      return lvl || '';
  }
};

export default function RepoAuthorizationBlotter() {
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const role = user?.role;

  const expectedLevelByRole = {
    front_office: 'front_office',
    back_office_verifier: 'back_office_verifier',
    back_office_final: 'back_office_final',
    admin: null
  };

  const expectedLevel = expectedLevelByRole[role] ?? null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deals, setDeals] = useState([]);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectCommentById, setRejectCommentById] = useState({});
  const [actingId, setActingId] = useState(null);
  const [limitExceededInfo, setLimitExceededInfo] = useState(null);
  const [settlementModes, setSettlementModes] = useState([]);
  const [viewingDeal, setViewingDeal] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  // Final-approved deals (for the Confirmations section below the approval
  // queue) - drawn from the same fetch as the pending queue rather than a
  // second API call, since repoDealsApi.getRecent already returns deals at
  // every approval level.
  const [finalApprovedDeals, setFinalApprovedDeals] = useState([]);

  const fetchDeals = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const all = await repoDealsApi.getRecent(100);
      const filtered = (all || []).filter((d) => {
        const approvalStatus = d.approval_status || 'pending';
        const level = d.current_approval_level || 'front_office';
        if (approvalStatus !== 'pending') return false;
        if (role === 'admin') return level !== 'final_approved' && level !== 'rejected';
        return expectedLevel ? level === expectedLevel : false;
      });
      setDeals(filtered);
      setFinalApprovedDeals((all || []).filter((d) => d.current_approval_level === 'final_approved').slice(0, 50));
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load repo deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchSettlementModes = async () => {
      try {
        const response = await api.get('/payment-master/modes');
        const modes = response.data?.data || [];
        setSettlementModes(Array.isArray(modes) ? modes : []);
      } catch (error) {
        console.error('Error fetching settlement modes for repo auth blotter:', error);
        setSettlementModes([]);
      }
    };
    fetchSettlementModes();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB');
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (!isFinite(num)) return '';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatSettlementMode = (code) => {
    if (!code) return '';
    const mode = settlementModes.find((m) => {
      const bankCode = m.bank_payment_code || m.BankPaymentCode || '';
      return bankCode === code;
    });
    if (!mode) return code;
    const method = mode.payment_method || mode.PaymentMethod || '';
    return method || code;
  };

  const doAction = async (id, action) => {
    setActingId(id);
    setError('');
    setSuccess('');
    setLimitExceededInfo(null);
    try {
      const comment = action === 'rejected' ? (rejectCommentById[id] || '').trim() : '';
      if (action === 'rejected' && !comment) {
        setError('Comment is required when rejecting');
        return;
      }
      await repoDealsApi.updateApproval(id, { action, comment }, user);
      const deal = deals.find((item) => item.id === id);
      setSuccess(`Repo deal ${deal?.deal_number || id} ${action === 'approved' ? 'approved' : 'rejected'} successfully`);
      setRejectingId(null);
      setRejectCommentById((prev) => ({ ...prev, [id]: '' }));
      await fetchDeals();
    } catch (e) {
      if (e?.response?.data?.limitExceeded) {
        setLimitExceededInfo(e.response.data);
      } else {
        setError(e?.response?.data?.message || e.message || 'Action failed');
      }
    } finally {
      setActingId(null);
    }
  };

  const openViewModal = async (id) => {
    setViewLoading(true);
    setError('');
    try {
      const deal = await repoDealsApi.getById(id);
      if (!deal) {
        setError('Failed to load repo deal details');
        return;
      }
      setViewingDeal(deal);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load repo deal details');
    } finally {
      setViewLoading(false);
    }
  };

  const canSee = Array.isArray(user?.allowed_tabs) && user.allowed_tabs.includes('repo');
  if (!canSee) {
    return (
      <div className="sister-blotter">
        <h2 style={{ margin: 0, marginBottom: 8 }}>Repo Authorization</h2>
        <div>You don&apos;t have permission to authorize Repo deals.</div>
      </div>
    );
  }

  return (
    <div className="sister-blotter">
      <h2 style={{ fontSize: '2rem', marginBottom: 16 }}>Repo Authorization Blotter</h2>
      <div style={{ marginBottom: 12, color: '#475569' }}>
        Current level: <b>{role === 'admin' ? 'Admin (all levels)' : levelLabel(expectedLevel)}</b>
      </div>

      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red', marginBottom: 16, padding: 10, background: '#ffe6e6', borderRadius: 4 }}>{error}</div>}
      {success && <div style={{ color: 'green', marginBottom: 16, padding: 10, background: '#e6ffe6', borderRadius: 4 }}>{success}</div>}
      <LimitExceededNotice info={limitExceededInfo} onDismiss={() => setLimitExceededInfo(null)} />

      {!loading && deals.length === 0 && <div>No repo deals pending your approval.</div>}

      {!loading && deals.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
            <thead style={{ background: '#f1f3f7' }}>
              <tr>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Deal Number</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Deal Type</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Value Date</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Maturity Date</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'right' }}>Rate</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'right' }}>Amount</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Counterparty</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Settlement Mode</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Current Approval Level</th>
                <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((d) => (
                <tr key={d.id}>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{d.deal_number || d.id}</td>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{d.deal_type || ''}</td>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{formatDate(d.value_date)}</td>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{formatDate(d.maturity_date)}</td>
                  <td style={{ padding: 8, border: '1px solid #eee', textAlign: 'right' }}>{d.rate ? Number(d.rate).toFixed(2) : ''}</td>
                  <td style={{ padding: 8, border: '1px solid #eee', textAlign: 'right' }}>{formatCurrency(d.principal_amount)}</td>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{d.counterparty_name || ''}</td>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{formatSettlementMode(d.settlement_mode)}</td>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{levelLabel(d.current_approval_level || 'front_office')}</td>
                  <td style={{ padding: 8, border: '1px solid #eee', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => openViewModal(d.id)}
                        disabled={actingId === d.id || viewLoading}
                        style={{
                          padding: '6px 14px',
                          background: '#0d6efd',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          cursor: actingId === d.id || viewLoading ? 'not-allowed' : 'pointer',
                          opacity: actingId === d.id || viewLoading ? 0.6 : 1
                        }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => doAction(d.id, 'approved')}
                        disabled={actingId === d.id}
                        style={{
                          padding: '6px 14px',
                          background: '#28a745',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          cursor: actingId === d.id ? 'not-allowed' : 'pointer',
                          opacity: actingId === d.id ? 0.6 : 1
                        }}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => setRejectingId((prev) => (prev === d.id ? null : d.id))}
                        disabled={actingId === d.id}
                        style={{
                          padding: '6px 14px',
                          background: '#dc3545',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          cursor: actingId === d.id ? 'not-allowed' : 'pointer',
                          opacity: actingId === d.id ? 0.6 : 1
                        }}
                      >
                        Reject
                      </button>
                    </div>
                    {rejectingId === d.id && (
                      <div style={{ marginTop: 8, padding: 10, background: '#fff3cd', borderRadius: 4, border: '1px solid #ffc107' }}>
                        <textarea
                          value={rejectCommentById[d.id] || ''}
                          onChange={(e) => setRejectCommentById((prev) => ({ ...prev, [d.id]: e.target.value }))}
                          placeholder="Enter rejection reason..."
                          style={{ width: '100%', minHeight: 60, padding: 8, borderRadius: 4, border: '1px solid #ccc', marginBottom: 8 }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => doAction(d.id, 'rejected')}
                            disabled={actingId === d.id || !(rejectCommentById[d.id] || '').trim()}
                            style={{
                              padding: '6px 14px',
                              background: '#dc3545',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              cursor: actingId === d.id ? 'not-allowed' : 'pointer',
                              opacity: actingId === d.id ? 0.6 : 1
                            }}
                          >
                            Confirm Reject
                          </button>
                          <button
                            onClick={() => setRejectingId(null)}
                            disabled={actingId === d.id}
                            style={{
                              padding: '6px 14px',
                              background: '#6c757d',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              cursor: actingId === d.id ? 'not-allowed' : 'pointer',
                              opacity: actingId === d.id ? 0.6 : 1
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {finalApprovedDeals.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{ fontSize: '1.25rem', marginBottom: 8 }}>Final Approved — Confirmations</h3>
          <p style={{ color: '#666', fontSize: 14, marginBottom: 12 }}>
            Deal confirmations can be generated once a repo deal has completed final approval ({finalApprovedDeals.length} deal{finalApprovedDeals.length === 1 ? '' : 's'}).
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f1f3f7' }}>
                <tr>
                  <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Deal Number</th>
                  <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Deal Type</th>
                  <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Counterparty</th>
                  <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'right' }}>Principal Amount</th>
                  <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'left' }}>Maturity Date</th>
                  <th style={{ padding: 8, border: '1px solid #eee', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {finalApprovedDeals.map((d) => (
                  <tr key={d.id}>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>{d.deal_number}</td>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>{d.deal_type}</td>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>{d.counterparty_name || ''}</td>
                    <td style={{ padding: 8, border: '1px solid #eee', textAlign: 'right' }}>{formatCurrency(d.principal_amount)}</td>
                    <td style={{ padding: 8, border: '1px solid #eee' }}>{d.maturity_date ? String(d.maturity_date).split('T')[0] : ''}</td>
                    <td style={{ padding: 8, border: '1px solid #eee', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => openViewModal(d.id)}
                          style={{ padding: '6px 14px', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                        >
                          View
                        </button>
                        <DealConfirmationButton
                          dealType="repo"
                          dealId={d.id}
                          refNumberHint={d.deal_number}
                          className=""
                          style={{ padding: '6px 14px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewingDeal && (
        <DealSlipViewModal
          dealType="Repo"
          transaction={{
            ...viewingDeal,
            // Surface fields under the names DealSlipViewModal expects
            principal_amount: viewingDeal.principal_amount,
            maturity_value: viewingDeal.maturity_amount,
            face_value: viewingDeal.face_value,
            collateral_face_value: viewingDeal.face_value || viewingDeal.face_value_as_per_counterparty,
            status: viewingDeal.approval_status,
            current_approval_level: viewingDeal.current_approval_level,
          }}
          onClose={() => setViewingDeal(null)}
        />
      )}
    </div>
  );
}

